import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useCreateItemMutation, useLazyGetPreSignedUrlQuery } from "../../services/items/items";
import { CelularSpecs, ItemType, ProductRelation } from "../../types/types";
import { uploadImage } from "../../utils/uploadImage";
import { resolveAssetUrl } from "../../utils/assets";
import { getErrorStatus, HTTP_CONFLICT } from "../../utils/apiError";
import { onlyNumbers } from "../shared/utils";
import CelularSpecsEditor, { EMPTY_SPECS } from "./CelularSpecsEditor";
import RelatedProductsEditor from "./RelatedProductsEditor";
import {
  CATEGORY_LABELS,
  DISCOUNT_RANGE_ERROR,
  INPUT_LABEL_PROPS,
  ITEM_CREATE_TEXTS,
  FORM_LABELS,
  IMAGE_BORDER_RADIUS,
} from "../shared/constants";
import { FormFieldGrid, FormFieldGridResponsive, SaveActionBar } from "../shared/styledComponents";
import { styled } from "@mui/material/styles";

const FormContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(1),
}));

const ItemCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const [createItem, { isLoading }] = useCreateItemMutation();
  const [getPreSignedUrl] = useLazyGetPreSignedUrlQuery();

  const [id, setItemID] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [availableQuantity, setAvailableQuantity] = useState<string>("");
  const [leadTimeDays, setLeadTimeDays] = useState<string>("");
  const [discount, setDiscount] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [price, setPrice] = useState<string>("");
  const [tipoProducto, setTipoProducto] = useState<string>("");
  const [rating, setRating] = useState<number | null>(null);
  const [marca, setMarca] = useState<string>("");
  const [modelo, setModelo] = useState<string>("");
  const [variante, setVariante] = useState<string>("");
  const [condicion, setCondicion] = useState<string>("");
  const [garantiaMeses, setGarantiaMeses] = useState<string>("");
  const [precioReferenciaCop, setPrecioReferenciaCop] = useState<string>("");
  const [urlOficial, setUrlOficial] = useState<string>("");
  const [specs, setSpecs] = useState<CelularSpecs>({ ...EMPTY_SPECS });

  const [productosRelacionados, setProductosRelacionados] = useState<ProductRelation[]>([]);
  const [pictureUrl, setPictureUrl] = useState<string>("");
  const [images, setImages] = useState<string[]>([]);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const imageMainInputRef = useRef<HTMLInputElement | null>(null);
  const imageGalleryInputRef = useRef<HTMLInputElement | null>(null);

  const openMainPicker = () => {
    setErrorMsg(null);
    if (!id.trim()) {
      setErrorMsg(ITEM_CREATE_TEXTS.idRequired);
      return;
    }
    imageMainInputRef.current?.click();
  };

  const openGalleryPicker = () => {
    setErrorMsg(null);
    if (!id.trim()) {
      setErrorMsg(ITEM_CREATE_TEXTS.idRequired);
      return;
    }
    imageGalleryInputRef.current?.click();
  };

  const handleMainSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setErrorMsg(null);
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);

      const presigned = await getPreSignedUrl({ id, _ts: Date.now() }).unwrap();
      await uploadImage(file, presigned.uploadUrl, presigned.headers["Content-Type"]);

      // The row stores the key, not a URL (EB-515).
      setPictureUrl(presigned.key);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : ITEM_CREATE_TEXTS.uploadMainError);
    } finally {
      setUploading(false);
      if (imageMainInputRef.current) imageMainInputRef.current.value = "";
    }
  };

  const handleGallerySelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setErrorMsg(null);
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);

      const presigned = await getPreSignedUrl({ id, _ts: Date.now() }).unwrap();
      await uploadImage(file, presigned.uploadUrl, presigned.headers["Content-Type"]);

      const nextImages = [...images, presigned.key];
      setImages(nextImages);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : ITEM_CREATE_TEXTS.uploadGalleryError);
    } finally {
      setUploading(false);
      if (imageGalleryInputRef.current) imageGalleryInputRef.current.value = "";
    }
  };

  const removeImageAt = (idx: number): void => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async (): Promise<void> => {
    setErrorMsg(null);

    if (!id.trim() || !name.trim() || !price.trim()) {
      setErrorMsg(ITEM_CREATE_TEXTS.requiredFields);
      return;
    }

    const priceNumber = Number(price);
    const discountNumber = discount ? Number(discount) : 0;
    const availableQtyNumber = availableQuantity ? Number(availableQuantity) : 0;

    if (Number.isNaN(priceNumber) || Number.isNaN(discountNumber)) {
      setErrorMsg(ITEM_CREATE_TEXTS.numericError);
      return;
    }

    if (discountNumber < 0 || discountNumber > 100) {
      setErrorMsg(DISCOUNT_RANGE_ERROR);
      return;
    }

    try {
      await createItem({
        id,
        name,
        description,
        available_quantity: availableQtyNumber,
        discount: discountNumber,
        is_active: isActive,
        price: priceNumber,
        picture: pictureUrl,
        images,
        // Emptiness, not truthiness: `0` days is a real promise, and `...("0" && …)` would keep it
        // while `...(0 && …)` would drop it. The string check makes that impossible to get wrong.
        ...(leadTimeDays.trim() !== "" && { lead_time_days: Number(leadTimeDays) }),
        ...(tipoProducto && { tipo_producto: tipoProducto as ItemType["tipo_producto"] }),
        ...(rating != null && { rating }),
        ...(marca && { marca }),
        ...(modelo && { modelo }),
        ...(variante && { variante }),
        ...(condicion && { condicion }),
        ...(garantiaMeses && { garantia_meses: Number(garantiaMeses) }),
        ...(precioReferenciaCop && { precio_referencia_cop: Number(precioReferenciaCop) }),
        ...(urlOficial && { url_oficial: urlOficial }),
        ...(tipoProducto === "celular" ? { specs } : {}),
        ...(productosRelacionados.length > 0 && {
          productos_relacionados: productosRelacionados,
        }),
      }).unwrap();

      navigate(`/item-maintenance/${id}`);
    } catch (err) {
      // The API now refuses to overwrite an existing id (409) instead of silently replacing a
      // curated product, so the admin has to be told to pick a different one.
      if (getErrorStatus(err) === HTTP_CONFLICT) {
        setErrorMsg(ITEM_CREATE_TEXTS.duplicateIdError);
        return;
      }
      setErrorMsg(err instanceof Error ? err.message : ITEM_CREATE_TEXTS.createError);
    }
  };

  return (
    <FormContainer p={2}>
      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
          {ITEM_CREATE_TEXTS.formTitle}
        </Typography>
      </Box>

      {errorMsg && (
        <Box mb={2}>
          <Alert severity="error">{errorMsg}</Alert>
        </Box>
      )}

      <TextField
        label={ITEM_CREATE_TEXTS.idLabel}
        value={id}
        onChange={(e) => setItemID(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        InputLabelProps={INPUT_LABEL_PROPS}
      />

      <Box
        mb={2}
        p={1}
        sx={{
          bgcolor: "background.default",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          {ITEM_CREATE_TEXTS.imagesTitle}
        </Typography>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            onClick={openMainPicker}
            variant="outlined"
            disabled={uploading}
            startIcon={<AddPhotoAlternateIcon />}
            sx={{ fontWeight: "bold", boxShadow: 1, bgcolor: "white" }}
          >
            {uploading ? ITEM_CREATE_TEXTS.saving : ITEM_CREATE_TEXTS.mainImageButton}
          </Button>
          <Button
            onClick={openGalleryPicker}
            variant="outlined"
            disabled={uploading}
            startIcon={<AddPhotoAlternateIcon />}
            sx={{ fontWeight: "bold", boxShadow: 1, bgcolor: "white" }}
          >
            {uploading ? ITEM_CREATE_TEXTS.saving : ITEM_CREATE_TEXTS.galleryButton}
          </Button>
        </Box>

        {pictureUrl && (
          <Box mt={1}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {ITEM_CREATE_TEXTS.previewLabel}
            </Typography>
            <img
              src={resolveAssetUrl(pictureUrl)}
              alt="Vista previa"
              style={{ maxWidth: 200, borderRadius: IMAGE_BORDER_RADIUS }}
              referrerPolicy="no-referrer"
            />
          </Box>
        )}

        <Box mt={2}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {ITEM_CREATE_TEXTS.galleryLabel(images.length)}
          </Typography>
          {images.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {images.map((url, idx) => (
                <Chip
                  key={`${url}-${idx}`}
                  label={url}
                  onDelete={() => removeImageAt(idx)}
                  sx={{ maxWidth: 300 }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {ITEM_CREATE_TEXTS.noImages}
            </Typography>
          )}
        </Box>
      </Box>

      <input
        ref={imageMainInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleMainSelected}
      />
      <input
        ref={imageGalleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleGallerySelected}
      />

      <FormFieldGrid>
        <TextField
          label={FORM_LABELS.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <TextField
          label={FORM_LABELS.description}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={3}
          InputLabelProps={INPUT_LABEL_PROPS}
        />
      </FormFieldGrid>

      <FormFieldGridResponsive sx={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <FormControl fullWidth size="small">
          <InputLabel {...INPUT_LABEL_PROPS}>{FORM_LABELS.tipoProducto}</InputLabel>
          <Select
            value={tipoProducto}
            onChange={(e) => setTipoProducto(e.target.value)}
            label={FORM_LABELS.tipoProducto}
          >
            <MenuItem value="">
              <em>Sin categoría</em>
            </MenuItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={FORM_LABELS.availableQuantity}
          value={availableQuantity}
          onChange={(e) => setAvailableQuantity(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <TextField
          label={FORM_LABELS.leadTimeDays}
          value={leadTimeDays}
          onChange={(e) => setLeadTimeDays(onlyNumbers(e.target.value))}
          size="small"
          fullWidth
          inputMode="numeric"
          helperText={FORM_LABELS.leadTimeDaysHelp}
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <TextField
          label={FORM_LABELS.discount}
          value={discount}
          onChange={(e) => setDiscount(onlyNumbers(e.target.value))}
          size="small"
          fullWidth
          inputMode="numeric"
          helperText="0-100%"
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <FormControlLabel
          control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
          label={FORM_LABELS.active}
        />

        <TextField
          label={FORM_LABELS.price}
          value={price}
          onChange={(e) => setPrice(onlyNumbers(e.target.value))}
          size="small"
          fullWidth
          inputMode="numeric"
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <Box>
          <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ mb: 0.5 }}>
            Rating
          </Typography>
          <Rating value={rating} precision={0.5} onChange={(_, newValue) => setRating(newValue)} />
        </Box>
      </FormFieldGridResponsive>

      <Box mt={2}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight="bold">
              {FORM_LABELS.metadataAccordion}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormFieldGridResponsive>
              <TextField
                label={FORM_LABELS.marca}
                value={marca}
                onChange={(e) => setMarca(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={INPUT_LABEL_PROPS}
              />
              <TextField
                label={FORM_LABELS.modelo}
                value={modelo}
                onChange={(e) => setModelo(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={INPUT_LABEL_PROPS}
              />
              <TextField
                label={FORM_LABELS.variante}
                value={variante}
                onChange={(e) => setVariante(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={INPUT_LABEL_PROPS}
              />
              <TextField
                label={FORM_LABELS.condicion}
                value={condicion}
                onChange={(e) => setCondicion(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={INPUT_LABEL_PROPS}
              />
              <TextField
                label={FORM_LABELS.garantiaMeses}
                value={garantiaMeses}
                onChange={(e) => setGarantiaMeses(onlyNumbers(e.target.value))}
                size="small"
                fullWidth
                inputMode="numeric"
                InputLabelProps={INPUT_LABEL_PROPS}
              />
              <TextField
                label={FORM_LABELS.precioReferenciaCop}
                value={precioReferenciaCop}
                onChange={(e) => setPrecioReferenciaCop(onlyNumbers(e.target.value))}
                size="small"
                fullWidth
                inputMode="numeric"
                InputLabelProps={INPUT_LABEL_PROPS}
              />
              <TextField
                label={FORM_LABELS.urlOficial}
                value={urlOficial}
                onChange={(e) => setUrlOficial(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={INPUT_LABEL_PROPS}
              />
            </FormFieldGridResponsive>
          </AccordionDetails>
        </Accordion>
      </Box>

      {tipoProducto === "celular" && (
        <Box mt={2}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight="bold">
                {FORM_LABELS.specsAccordion}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CelularSpecsEditor specs={specs} onChange={setSpecs} />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/*
        Same Box-and-heading shape the edit form uses, not an Accordion (EB-525). The two forms
        sit side by side in an admin's day and the section should look identical in both; the
        ticket said Accordion, but the edit form never used one here.
      */}
      <Box mt={3}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          {FORM_LABELS.relatedProductsSection}
        </Typography>
        <RelatedProductsEditor
          relations={productosRelacionados}
          onChange={setProductosRelacionados}
        />
      </Box>

      <SaveActionBar>
        <Button
          onClick={() => void handleSave()}
          disabled={isLoading}
          variant="outlined"
          color="success"
          startIcon={<SaveIcon />}
          fullWidth
          sx={{
            fontWeight: "bold",
            boxShadow: 2,
            maxWidth: "40%",
            bgcolor: "white",
          }}
        >
          {isLoading ? ITEM_CREATE_TEXTS.saving : ITEM_CREATE_TEXTS.createButton}
        </Button>
      </SaveActionBar>
    </FormContainer>
  );
};

export default ItemCreateForm;
